import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  toggleUserActive,
  resetUserPassword,
  getRoles,
  getAuditLogs,
} from '@/api/adminApi';
import type { UserFilters, AuditLogFilters, CreateUserRequest, UpdateUserRequest } from '@/types/admin';

export function useUsers(mdFilters: UserFilters) {
  return useQuery({
    queryKey: ['admin', 'users', mdFilters],
    queryFn: () => getUsers(mdFilters),
    placeholderData: (mdPrevious) => mdPrevious,
  });
}

export function useUserById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'users', mdId],
    queryFn: () => getUserById(mdId!),
    enabled: !!mdId,
  });
}

export function useCreateUser() {
  const mdQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (mdData: CreateUserRequest) => createUser(mdData),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useUpdateUser() {
  const mdQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (mdData: UpdateUserRequest) => updateUser(mdData),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useToggleActive() {
  const mdQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (mdId: string) => toggleUserActive(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (mdId: string) => resetUserPassword(mdId),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: getRoles,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAuditLogs(mdFilters: AuditLogFilters) {
  return useQuery({
    queryKey: ['admin', 'audit-logs', mdFilters],
    queryFn: () => getAuditLogs(mdFilters),
    placeholderData: (mdPrevious) => mdPrevious,
  });
}
